/**
 * One reading of a Company-route error for every screen that shows one.
 * The codes a user can act on get an instruction; anything else shows the
 * server's own message.
 *
 * Some refusals arrive with their code on the error (`ConflictError` and
 * friends carry one); others arrive as a generic validation / forbidden
 * error whose MESSAGE starts with the code (`APPROVAL_OWNER_DENY: …`). Both
 * are read here so a screen never has to know which.
 */

const COMPANY_ERROR_TEXT: Record<string, string> = {
  MFA_REQUIRED_FOR_COMPANY:
    'Managing a Company requires two-factor authentication. Enable it in Account Settings, then try again.',
  MFA_REVALIDATION_REQUIRED: 'Your session predates a recent security change. Sign in again.',
  COMPANY_NAME_RESERVED:
    'That name is reserved (Company names cannot claim the Fias brand). Choose another.',
  COMPANY_OWNERS_CHANGED:
    'The Company’s owners changed since this screen was shown. Review the owners named now and accept again.',
  PAYER_STORAGE_QUOTA_EXCEEDED:
    'The Company’s payer does not have room in their storage plan for this workspace. The payer needs a larger plan (or less stored) first.',
  COMPANY_WORKSPACES_LIMIT: 'This Company already links as many workspaces as it can.',
  COMPANY_MEMBER_GUEST:
    'A guest account cannot join a Company. They need to finish signing up first.',
  COMPANY_MEMBER_INVITE_PENDING: 'That person already has an open invitation to this Company.',
  COMPANY_MEMBER_INVITES_TOO_MANY:
    'This Company has too many open invitations. Cancel some, or wait for people to answer.',
  COMPANY_MEMBER_INVITEE_BUSY:
    'That person has too many open Company invitations. They need to answer some first.',
  COMPANY_MEMBERS_LIMIT: 'This Company already has as many people as it can hold.',
  COMPANY_MEMBER_EXISTS: 'That person is already on this Company. Change their role instead.',
  APPROVAL_OWNER_DENY:
    'A workspace owner denied this arche here. Only an owner of this workspace can change that.',
  APPROVER_IS_COLLABORATOR:
    'You publish this arche, so someone else on this workspace or Company must approve it.',
};

/** The Company error code carried by `err`, from its `code` or its message prefix. */
export function companyErrorCode(err: unknown): string | null {
  const code = (err as { code?: unknown })?.code;
  if (typeof code === 'string' && code in COMPANY_ERROR_TEXT) return code;
  const message = err instanceof Error ? err.message : '';
  const prefix = /^([A-Z][A-Z0-9_]+):/.exec(message)?.[1];
  if (prefix && prefix in COMPANY_ERROR_TEXT) return prefix;
  return typeof code === 'string' ? code : null;
}

export function describeCompanyError(err: unknown, fallback = 'Please try again.'): string {
  const code = companyErrorCode(err);
  if (code === 'COMPANY_NAME_RESERVED' && err instanceof Error) {
    // The server names the rule the name broke; show it after the code.
    const detail = err.message.replace(/^COMPANY_NAME_RESERVED:\s*/, '');
    if (detail && detail !== err.message) return `That name is reserved: ${detail}`;
  }
  if (code && code in COMPANY_ERROR_TEXT) return COMPANY_ERROR_TEXT[code];
  return err instanceof Error ? err.message : fallback;
}
